import { Component, Input } from '@builder.io/sdk';

import { buttonIds } from 'molecules/Icon/types';
import sectionFields from 'molecules/Section/config';

const buttonIconEnum = ['No Icon', ...buttonIds] as const;

const tableCell = [
  {
    name: 'cell',
    type: 'string',
    enum: [
      { label: 'Check', value: 'check' },
      { label: 'Indiscriminate', value: 'indiscriminate' },
      { label: 'Text', value: 'text' },
    ],
    required: true,
    defaultValue: 'indiscriminate',
    helperText: 'Type of content for this cell: checkmark, empty (indiscriminate), or custom text.',
  },
  {
    name: 'text',
    type: 'string',
    showIf: "options.get('cell') === 'text'",
    helperText: 'Custom text displayed when "Text" is selected as the cell type.',
  },
] as const satisfies DeepReadonly<Input[]>;

const tableRow = [
  {
    name: 'rowHeading',
    type: 'string',
    helperText: 'Label for the feature or detail being compared in this row.',
  },
  {
    name: 'description',
    type: 'string',
    helperText: 'Optional description shown as a tooltip for this row.',
  },
  {
    name: 'tierOne',
    type: 'object',
    subFields: tableCell,
    helperText: 'Cell content for Tier One.',
  },
  {
    name: 'tierTwo',
    type: 'object',
    subFields: tableCell,
    helperText: 'Cell content for Tier Two.',
  },
  {
    name: 'tierThree',
    type: 'object',
    subFields: tableCell,
    helperText: 'Cell content for Tier Three.',
  },
] as const satisfies DeepReadonly<Input[]>;

const pricingTableFields = [
  {
    name: 'tableHeading',
    type: 'string',
    helperText: 'Main heading displayed above the pricing table.',
  },
  // Tier One
  {
    name: 'tierOneTitle',
    type: 'string',
    required: true,
    helperText: 'Title for Tier One (e.g., Pro).',
  },
  {
    name: 'tierOneLabel',
    type: 'string',
    defaultValue: 'Contact us',
    helperText: 'Button label for Tier One.',
  },
  {
    name: 'tierOneLink',
    type: 'string',
    helperText: 'CTA link for Tier One.',
  },
  {
    name: 'tierOneStartIcon',
    type: 'string',
    enum: buttonIconEnum,
    helperText: 'Optional icon displayed before the button label for Tier One.',
  },
  {
    name: 'tierOneEndIcon',
    type: 'string',
    enum: buttonIconEnum,
    helperText: 'Optional icon displayed after the button label for Tier One.',
  },
  {
    name: 'tierOneDescription',
    type: 'string',
    helperText: 'Short description for Tier One.',
  },
  {
    name: 'tierOneHighlighted',
    type: 'boolean',
    helperText: 'Highlight Tier One visually to draw attention.',
  },
  {
    name: 'tierOneChiliPiperUrl',
    type: 'string',
    helperText: 'Chili Piper Router Link URL for Tier One. When set, the button opens this URL in a popup window.',
  },
  // Tier Two
  {
    name: 'tierTwoTitle',
    type: 'string',
    required: true,
    helperText: 'Title for Tier Two (e.g., Scale).',
  },
  {
    name: 'tierTwoLabel',
    type: 'string',
    defaultValue: 'Contact us',
    helperText: 'Button label for Tier Two.',
  },
  {
    name: 'tierTwoLink',
    type: 'string',
    helperText: 'CTA link for Tier Two.',
  },
  {
    name: 'tierTwoStartIcon',
    type: 'string',
    enum: buttonIconEnum,
    helperText: 'Optional icon displayed before the button label for Tier Two.',
  },
  {
    name: 'tierTwoEndIcon',
    type: 'string',
    enum: buttonIconEnum,
    helperText: 'Optional icon displayed after the button label for Tier Two.',
  },
  {
    name: 'tierTwoDescription',
    type: 'string',
    helperText: 'Short description for Tier Two.',
  },
  {
    name: 'tierTwoHighlighted',
    type: 'boolean',
    helperText: 'Highlight Tier Two visually to draw attention.',
  },
  {
    name: 'tierTwoChiliPiperUrl',
    type: 'string',
    helperText: 'Chili Piper Router Link URL for Tier Two. When set, the button opens this URL in a popup window.',
  },
  // Tier Three
  {
    name: 'tierThreeTitle',
    type: 'string',
    required: true,
    helperText: 'Title for Tier Three (e.g., Enterprise).',
  },
  {
    name: 'tierThreeLabel',
    type: 'string',
    defaultValue: 'Contact us',
    helperText: 'Button label for Tier Three.',
  },
  {
    name: 'tierThreeLink',
    type: 'string',
    helperText: 'CTA link for Tier Three.',
  },
  {
    name: 'tierThreeStartIcon',
    type: 'string',
    enum: buttonIconEnum,
    helperText: 'Optional icon displayed before the button label for Tier Three.',
  },
  {
    name: 'tierThreeEndIcon',
    type: 'string',
    enum: buttonIconEnum,
    helperText: 'Optional icon displayed after the button label for Tier Three.',
  },
  {
    name: 'tierThreeDescription',
    type: 'string',
    helperText: 'Short description for Tier Three.',
  },
  {
    name: 'tierThreeHighlighted',
    type: 'boolean',
    helperText: 'Highlight Tier Three visually to draw attention.',
  },
  {
    name: 'tierThreeChiliPiperUrl',
    type: 'string',
    helperText: 'Chili Piper Router Link URL for Tier Three. When set, the button opens this URL in a popup window.',
  },
  {
    name: 'tableSections',
    type: 'list',
    subFields: [
      {
        name: 'id',
        type: 'string',
        required: true,
        helperText: 'Auto-generated unique ID for each section.',
      },
      {
        name: 'label',
        type: 'string',
        defaultValue: 'Plan Details',
        helperText: 'Section label (e.g., Features, Support, Integrations).',
      },
      {
        name: 'rows',
        type: 'list',
        subFields: tableRow,
        helperText: 'Rows of features compared across Tier One, Tier Two, and Tier Three.',
      },
    ],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (options: Map<string, any>) => {
      if (options.get('tableSections').length > 0) {
        options.get('tableSections').forEach((item: Map<string, string>, index: number) => {
          item.set('id', `tableSections-${index}`);
        });
      }
    },
    helperText: 'List of sections in the pricing table. Each section contains multiple rows of features.',
  },
  ...sectionFields,
] as const satisfies DeepReadonly<Input[]>;

const pricingTableConfig = {
  name: 'v2: Pricing Table',
  inputs: pricingTableFields,
} as Component;

export type PricingCellProps = GenerateBuilder<typeof tableCell>;
export type PricingRowProps = GenerateBuilder<typeof tableRow>;
export type PricingTableConfigProps = GenerateBuilder<typeof pricingTableFields>;

export default pricingTableConfig;
